import React, { createContext, useRef, useState, useEffect } from "react";
import { createWebSocket, WS_CLOSE_AUTH_REJECTED } from "../utils/websocket";
import { getUserType } from "../utils/authutils";
import { ensureFreshSession } from "../utils/api";

const WS_BASE_URL = process.env.REACT_APP_WS_BASE_URL;
const WS_CHAT_ENDPOINT = process.env.REACT_APP_WS_CHAT_ENDPOINT || "chat/";
const RECONNECT_DELAY_MS = 5000;

export const WebSocketContext = createContext();

// The access token is HttpOnly and cannot be read by JS. The browser sends the
// access_token cookie automatically on the WS upgrade handshake (same-domain),
// so we no longer append ?auth_token to the URL (contract §9). `connected`
// signals whether a session exists and we should open the socket.
export const WebSocketProvider = ({ children, connected }) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);

  useEffect(() => {
    if (!connected || !WS_BASE_URL) return;
    const userType = getUserType();
    if (userType === "admin") return;
    let stopped = false;

    const clearReconnectTimeout = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const connect = () => {
      if (stopped) return;
      if (typeof window !== "undefined" && !window.navigator.onLine) {
        setIsConnected(false);
        return;
      }

      const handleOpen = () => {
        console.log("WebSocket connection established");
        clearReconnectTimeout();
        setIsConnected(true);
      };
      const handleClose = (event) => {
        console.log("WebSocket disconnected");
        setIsConnected(false);

        if (stopped) return;
        wsRef.current = null;
        if (typeof window !== "undefined" && !window.navigator.onLine) return;
        clearReconnectTimeout();
        if (event?.code === WS_CLOSE_AUTH_REJECTED) {
          ensureFreshSession()
            .then(() => {
              if (stopped) return;
              reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
            })
            .catch(() => {
              stopped = true;
            });
          return;
        }
        reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
      };
      const handleError = (error) => {
        console.error("WebSocket error:", error);
      };
      const handleMessage = (event) => {
        try {
          const wsMsg = JSON.parse(event.data);
          if (wsMsg.type === "chat_message" && wsMsg.data) {
            setChatMessages((prev) => [...prev, wsMsg.data]);
            console.log("Received chat message:", wsMsg.data);
          }
        } catch (err) {
          console.error("Error parsing WebSocket message:", err);
        }
      };

      try {
        // Cookie-authenticated handshake: no auth_token in the URL (contract §9).
        const wsUrl = new URL(WS_CHAT_ENDPOINT, WS_BASE_URL);
        wsRef.current = createWebSocket({
          wsBaseUrl: wsUrl.toString(),
          onOpen: handleOpen,
          onMessage: handleMessage,
          onError: handleError,
          onClose: handleClose,
        });
      } catch (err) {
        console.error("Error creating WebSocket URL:", err);
      }
    };

    const handleOffline = () => {
      setIsConnected(false);
      clearReconnectTimeout();
      wsRef.current?.close();
      wsRef.current = null;
    };

    const handleOnline = () => {
      if (stopped || wsRef.current) return;
      clearReconnectTimeout();
      connect();
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    connect();

    return () => {
      stopped = true;
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      clearReconnectTimeout();
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connected]);

  return (
    <WebSocketContext.Provider
      value={{
        wsRef,
        isConnected,
        chatMessages,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
